import { useEffect } from "react";

const DEFAULTS = {
  title: "Zeel Beauty — Skincare, figured out together.",
  description: "Skincare, figured out together. Find routines that worked for people with skin like yours — honest reviews and real results, shared by people who've been where you are.",
  ogTitle: "Zeel Beauty — Skincare, figured out together",
  ogDescription: "Find routines that worked for skin like yours. Shared by real people. Backed by the community.",
  ogImage: "https://zeel.beauty/og-image.jpg",
  ogUrl: "https://zeel.beauty/",
  canonical: "https://zeel.beauty/",
  robots: "index, follow",
};

function setMeta(attr, key, value) {
  const selector = `meta[${attr}="${key}"]`;
  const el = document.querySelector(selector);
  if (el) {
    el.setAttribute("content", value);
  }
}

function setCanonical(href) {
  const el = document.querySelector('link[rel="canonical"]');
  if (el) el.setAttribute("href", href);
}

export function useSEO({ title, description, ogTitle, ogDescription, ogImage, ogUrl, canonical, jsonLd, robots } = {}) {
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta("name", "description", description);
    if (ogTitle || title) {
      setMeta("property", "og:title", ogTitle || title);
      setMeta("name", "twitter:title", ogTitle || title);
    }
    if (ogDescription || description) {
      setMeta("property", "og:description", ogDescription || description);
      setMeta("name", "twitter:description", ogDescription || description);
    }
    if (ogImage) setMeta("property", "og:image", ogImage);
    if (ogUrl || canonical) setMeta("property", "og:url", ogUrl || canonical);
    if (canonical) setCanonical(canonical);
    // Per-page `<meta name="robots">` override — used to noindex AI profile pages
    // while the editorial pipeline is being tuned. Resets to "index, follow" on unmount.
    if (robots) setMeta("name", "robots", robots);

    // JSON-LD structured data
    if (jsonLdString) {
      let scriptEl = document.querySelector('script[data-seo-jsonld]');
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.type = 'application/ld+json';
        scriptEl.setAttribute('data-seo-jsonld', 'true');
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = jsonLdString;
    }

    return () => {
      document.title = DEFAULTS.title;
      setMeta("name", "description", DEFAULTS.description);
      setMeta("property", "og:title", DEFAULTS.ogTitle);
      setMeta("property", "og:description", DEFAULTS.ogDescription);
      setMeta("property", "og:image", DEFAULTS.ogImage);
      setMeta("property", "og:url", DEFAULTS.ogUrl);
      setMeta("name", "twitter:title", DEFAULTS.ogTitle);
      setMeta("name", "twitter:description", DEFAULTS.ogDescription);
      setCanonical(DEFAULTS.canonical);
      setMeta("name", "robots", DEFAULTS.robots);
      // Remove JSON-LD script
      const scriptEl = document.querySelector('script[data-seo-jsonld]');
      if (scriptEl) scriptEl.remove();
    };
  }, [title, description, ogTitle, ogDescription, ogImage, ogUrl, canonical, jsonLdString, robots]);
}
